import { TFunction } from "i18next";
import { get } from "lodash";
import { ReactElement, ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  TextCellTemplateReports,
  useDecorateWithMarkLocal,
} from "@ryde/controls";
import { useReportsContext } from "@ryde/redux";
import {
  CurrencySign,
  currencySignSelector,
  IColumnDef,
  IReportGridColumn,
  IRydeCellContext,
  IRydeColumnHelper,
  ITableDto,
  normalizeReportGridValue,
  resolveReportCellColorClass,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { CellFooterSummary, CellReportsSortable } from "./cells";

interface IUseReportTableDefColumnsReturn {
  dynamicReportColumns: IColumnDef<ITableDto, unknown>[];
}

interface IUseReportsTableDefColumns {
  reportColumnHelper: IRydeColumnHelper<ITableDto>;
}

const resolveReportColumnFooterPrefix = ({
  t,
  prefixTotalTranslationKey,
  isPrice,
  isPercentage,
  currencySign,
}: {
  t: TFunction<"translation", undefined>;
  currencySign: CurrencySign;
  isPrice: boolean | undefined;
  isPercentage: boolean | undefined;
  prefixTotalTranslationKey: string | undefined;
}): string => {
  if (isPrice) {
    return currencySign;
  }

  if (isPercentage) {
    return "%";
  }

  return prefixTotalTranslationKey ? t(prefixTotalTranslationKey) : "";
};

const resolveColumnHeaderText = ({
  t,
  uiTableColumn,
  translationKey,
}: {
  t: TFunction<"translation", undefined>;
  translationKey: string;
  currencySign: CurrencySign;
  uiTableColumn: string;
  isPrice: boolean | undefined;
  isPercentage: boolean | undefined;
}): string => {
  const result = translationKey
    ? t(translationKey)
    : t(`gridLabel.${uiTableColumn}`);

  /* commented for RYDE-3909, - but may be needed later
  if (isPrice) {
    return `${result}, ${currencySign}`;
  }
*/
  return result;
};

export const useTableDefReportsColumns = ({
  reportColumnHelper,
}: IUseReportsTableDefColumns): IUseReportTableDefColumnsReturn => {
  const { reportDto } = useReportsContext();
  const { t } = useTranslation();
  const { timezone } = useTimezone();

  const { layout } = reportDto || {};
  const { decorateValue } = useDecorateWithMarkLocal();
  const currencySign = useRecoilValue(currencySignSelector);
  const { formatDateByGroupingPeriod } = useFormats();

  const cellValueDecoratedGetter = useCallback(
    (
      info: IRydeCellContext<ITableDto, unknown>,
      reportGridColumn: IReportGridColumn,
    ): ReactElement | unknown => {
      const value = info.getValue();

      if (reportGridColumn.isPeriodGroupingColumn) {
        return formatDateByGroupingPeriod(
          value as TDateISO,
          reportGridColumn?.periodGrouping,
        );
      }

      const valueNormalized = normalizeReportGridValue({
        value,
        reportGridColumn,
        timezone,
        t,
      });
      return decorateValue(valueNormalized as ReactNode);
    },
    [decorateValue, formatDateByGroupingPeriod, t, timezone],
  );
  const dynamicReportColumns = useMemo(() => {
    if (!layout?.columns?.length) {
      return [];
    }

    return layout?.columns?.map(reportGridColumn => {
      const {
        uiTableColumn,
        columnTitleTranslationKey,
        columnWidth,
        prefixTotalTranslationKey,
        prefixTotalDelimiter,
        isPrice,
        isPercentage,
        isHiddenFromView,
      } = reportGridColumn;
      const columnHeaderTranslated = resolveColumnHeaderText({
        t,
        translationKey: columnTitleTranslationKey,
        uiTableColumn,
        isPrice,
        isPercentage,
        currencySign,
      });
      const columnFooterPrefixTranslated = resolveReportColumnFooterPrefix({
        t,
        prefixTotalTranslationKey,
        isPrice,
        isPercentage,
        currencySign,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const colAccessor: IColumnDef<ITableDto, any> = {
        id: uiTableColumn,
        header: () => (
          <CellReportsSortable
            fieldNameResolved={uiTableColumn}
            groupingDisabled={true}
            sortingDisabled={false}
            isHiddenFromView={isHiddenFromView}
          >
            {columnHeaderTranslated}
          </CellReportsSortable>
        ),
        cell: info => {
          const reportTemplateId = reportDto?.reportTemplate?.id;
          const colorClassName = resolveReportCellColorClass({
            reportTemplateId,
            uiTableColumn,
            value: info.getValue(),
          });

          return (
            <TextCellTemplateReports
              key={`${columnTitleTranslationKey}_${uiTableColumn}`}
              isHiddenFromView={isHiddenFromView}
              value={cellValueDecoratedGetter(info, reportGridColumn)}
              name={uiTableColumn}
              className={colorClassName}
            />
          );
        },
        footer: () => (
          <CellFooterSummary
            columnFooterPrefix={columnFooterPrefixTranslated}
            prefixTotalDelimiter={prefixTotalDelimiter}
            reportDto={reportDto}
            reportGridColumn={reportGridColumn}
          />
        ),
        meta: {
          displayName: columnHeaderTranslated,
          columnWidth,
          isHiddenFromView,
        },
      };

      return reportColumnHelper.accessor((x: ITableDto) => {
        return get(x, uiTableColumn);
      }, colAccessor);
    });
  }, [
    layout?.columns,
    t,
    currencySign,
    reportColumnHelper,
    cellValueDecoratedGetter,
    reportDto,
  ]);

  return { dynamicReportColumns };
};
